import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function ReflexTest() {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState("waiting"); // waiting, playing, levelComplete, finished
  const [level, setLevel] = useState(1);
  const [targets, setTargets] = useState([]); 
  const [targetPos, setTargetPos] = useState({ x: 50, y: 50 }); 
  const [targetColor, setTargetColor] = useState("blue");
  const [score, setScore] = useState(0); // This now persists across levels
  const [timeLeft, setTimeLeft] = useState(15);
  const [bestScore, setBestScore] = useState(0);
  const [nextLevelCountdown, setNextLevelCountdown] = useState(5);
  const [isTargetVisible, setIsTargetVisible] = useState(false);

  const timerRef = useRef(null);

  const getRandomPos = () => ({
    x: Math.floor(Math.random() * 70) + 15,
    y: Math.floor(Math.random() * 60) + 20,
  });

  // GAME LOOP: PULSE SYSTEM
  useEffect(() => {
    let pulseInterval;
    if (gameState === "playing") {
      if (level === 2) {
        pulseInterval = setInterval(() => {
          setIsTargetVisible(false);
          setTimeout(() => {
            setTargetPos(getRandomPos());
            setTargetColor(Math.random() > 0.4 ? "green" : "red");
            setIsTargetVisible(true);
          }, 100);
        }, 900);
      } else if (level === 3) {
        pulseInterval = setInterval(() => {
          const newTargets = Array.from({ length: 5 }).map((_, i) => ({
            id: Date.now() + i,
            ...getRandomPos(),
            color: Math.random() > 0.7 ? "blue" : Math.random() > 0.5 ? "red" : "green",
            clicked: false
          }));
          setTargets(newTargets);
        }, 1200);
      }
    }
    return () => clearInterval(pulseInterval);
  }, [gameState, level]);

  const startGame = () => {
    setTimeLeft(15);
    setNextLevelCountdown(5);
    setGameState("playing");
    setTargets([]);
    
    if (level === 1) {
      setScore(0); // Reset total score only at the very start
      setTargetPos(getRandomPos());
      setTargetColor("blue");
      setIsTargetVisible(true);
    }
    
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setGameState(level === 3 ? "finished" : "levelComplete");
          setIsTargetVisible(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleTargetClick = (e, id = null) => {
    e.stopPropagation();
    
    if (level === 1) {
      setScore(prev => prev + 1);
      setTargetPos(getRandomPos());
    } else if (level === 2) {
      if (!isTargetVisible) return;
      setScore(prev => targetColor === "green" ? prev + 2 : Math.max(0, prev - 3));
      setIsTargetVisible(false);
    } else if (level === 3) {
      const clickedTarget = targets.find(t => t.id === id);
      if (!clickedTarget || clickedTarget.clicked) return;

      if (clickedTarget.color === "blue") {
        setScore(prev => prev + 5);
      } else {
        setScore(prev => Math.max(0, prev - 2));
      }
      setTargets(prev => prev.map(t => t.id === id ? { ...t, clicked: true } : t));
    }
  };

  // TRANSITION LOGIC
  useEffect(() => {
    let transitionTimer;
    if (gameState === "levelComplete") {
      transitionTimer = setInterval(() => {
        setNextLevelCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(transitionTimer);
            setLevel(prevLvl => prevLvl + 1);
            setGameState("waiting");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    if (gameState === "finished") {
        if (score > bestScore) setBestScore(score);
    }
    return () => clearInterval(transitionTimer);
  }, [gameState, score, bestScore]);

  return (
    <div className="fixed inset-0 z-[100] bg-[#090a0b] text-white flex flex-col items-center justify-center overflow-hidden select-none cursor-crosshair font-sans">
      
      {/* HUD */}
      <div className="absolute top-6 w-full px-6 md:px-12 flex justify-between items-start z-50">
        <div className="space-y-1">
          <p className="text-[10px] tracking-[0.4em] text-zinc-200 uppercase font-bold">Total </p>
          <h2 className="text-3xl font-black text-blue-500 tabular-nums">{score}</h2>
        </div>
        <div className="text-center">
           <p className="text-[10px] tracking-[0.4em] text-zinc-200 uppercase font-bold">Time</p>
           <h2 className={`text-3xl font-black tabular-nums ${timeLeft < 5 && gameState === 'playing' ? 'text-red-500 animate-pulse' : 'text-white'}`}>{timeLeft}s</h2>
        </div>
        <div className="text-right space-y-1">
          <p className="text-[10px] tracking-[0.4em] text-zinc-200 uppercase font-bold">Best</p>
          <h2 className="text-3xl font-black text-zinc-400 tabular-nums">{bestScore}</h2>
        </div>
      </div>

      <div className="relative w-full h-full flex items-center justify-center">
        
        {/* START LEVEL SCREEN */}
        {gameState === "waiting" && (
          <div className="text-center space-y-8 px-6 animate-in fade-in zoom-in duration-300">
<div className="space-y-2">
  <h1 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter">
    {level === 1
      ? 'Reaction'
      : level === 2
      ? 'Visual Reflex'
      : 'Speed Reflex'} <span className="text-blue-500">Test</span>
  </h1>

  <p className="text-[10px] md:text-xs text-zinc-200 tracking-[0.4em] uppercase max-w-xs mx-auto font-bold leading-loose">
    {level === 1 && 'Test your reaction time by tapping targets as fast as possible.'}
    {level === 2 && 'Visual reflex challenge. Tap GREEN (+2) and avoid RED (-3).'}
    {level === 3 && 'High speed reflex mode. Tap BLUE (+5) for maximum efficiency.'}
  </p>
</div>
            <button onClick={startGame} className="px-12 py-5 bg-blue-600 text-white text-xs tracking-[1em] uppercase font-black hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/40">
              Begin Level {level}
            </button>
          </div>
        )}

        {/* GAMEPLAY RENDERING */}
        {gameState === "playing" && level < 3 && isTargetVisible && (
          <TargetItem pos={targetPos} color={targetColor} onClick={handleTargetClick} />
        )}

        {gameState === "playing" && level === 3 && (
          targets.map(t => !t.clicked && (
            <TargetItem key={t.id} pos={{ x: t.x, y: t.y }} color={t.color} onClick={(e) => handleTargetClick(e, t.id)} />
          ))
        )}

        {/* INTER-LEVEL TRANSITION */}
        {gameState === "levelComplete" && (
          <div className="text-center space-y-8 animate-in fade-in duration-500">
            <div className="space-y-2">
              <h2 className="text-[10px] text-blue-500 tracking-[0.8em] uppercase font-black">Level {level} Secured</h2>
              <h1 className="text-6xl md:text-8xl font-black italic">{score}</h1>
            </div>
            <p className="text-[10px] tracking-[0.3em] text-blue-400 font-bold uppercase animate-pulse">
              Calibrating Level 0{level + 1} in {nextLevelCountdown}...
            </p>
          </div>
        )}

        {/* FINAL RESULTS SCREEN */}
        {gameState === "finished" && (
          <div className="text-center space-y-10 animate-in fade-in zoom-in duration-500 px-6">
            <div className="space-y-4">
                <div className="space-y-1">
                    <h2 className="text-[10px] text-blue-500 tracking-[0.8em] uppercase font-black">Final Neural Output</h2>
                    <h1 className="text-7xl md:text-9xl font-black italic text-white">{score}</h1>
                </div>
                
                <div className={`py-4 px-8 border-y ${score >= 100 ? 'border-green-500/30' : 'border-red-500/30'}`}>
                    <p className={`text-xs md:text-sm tracking-widest uppercase font-bold ${score >= 100 ? 'text-green-400' : 'text-red-400'}`}>
                        {score >= 100 
                            ? "Status: Optimized. High reflex precision detected." 
                            : "Status: Sub-Optimal. Reflex sync needs improvement."}
                    </p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button 
                    onClick={() => { setLevel(1); setGameState("waiting"); }} 
                    className="px-8 py-4 bg-zinc-800 text-white text-[10px] tracking-[0.4em] uppercase font-black hover:bg-zinc-700 transition-all"
                >
                    Restart Grid
                </button>
                <button 
                    onClick={() => navigate("/game/focus")} 
                    className="px-8 py-4 bg-blue-600 text-white text-[10px] tracking-[0.4em] uppercase font-black hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/30"
                >
                    Enter Focus Mode
                </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TargetItem({ pos, color, onClick }) {
  const colorMap = {
    blue: "bg-blue-500 border-blue-500 shadow-blue-500/50",
    green: "bg-green-500 border-green-500 shadow-green-500/50",
    red: "bg-red-500 border-red-500 shadow-red-500/50"
  };

  return (
    <button
      onPointerDown={onClick}
      className="absolute transition-all duration-75 ease-out outline-none scale-up-center"
      style={{ 
        left: `${pos.x}%`, top: `${pos.y}%`, 
        transform: 'translate(-50%, -50%)', padding: '25px' 
      }}
    >
      <div className="relative flex items-center justify-center">
        <div className={`absolute w-16 h-16 md:w-20 md:h-20 border-2 rounded-full animate-ping opacity-25 ${color === 'blue' ? 'border-blue-500' : color === 'green' ? 'border-green-500' : 'border-red-500'}`} />
        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full shadow-lg ${colorMap[color]}`}>
           <div className="w-full h-full rounded-full border border-white/20" />
        </div>
      </div>
    </button>
  );
}